import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, onMounted, ref } from "vue";
import Page from "@/components/shared/Page.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
} from "@/types";
import router from "@/router";
import RouteDemandSurveyEdit from "@/components/mypage/RouteDemandSurveyEdit.vue";
import RouteDemandSurveyDetail from "@/components/mypage/RouteDemandSurveyDetail.vue";
import { useSurveyStore } from "@/stores/surveyStore";
import { IFilter } from "@/domain/entities/filter/filter.types";
import { convertLineDemandSurveyToFilters } from "@/utils/convertLineDemandSurveyToFilters";
import {
  IAnswerProp,
  ILineDemandSurvey,
  ILineDemandSurveyAnswer,
} from "@/domain/entities/survey";
import Modal from "@/components/shared/modal/base/Modal.vue";
import { useUserStore } from "@/stores/userStore";


const __default__ = {
  name: "RouteDemandSurvey",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const surveyStore = useSurveyStore();
const userStore = useUserStore();

const userInfo = computed(() => userStore.userInfo);

const createMode = ref(false);
const editMode = ref(false);

const toggleCreateMode = (mode: boolean) => {
  createMode.value = mode;
};

const toggleEditMode = (mode: boolean) => {
  editMode.value = mode;
};

const handleClickBack = () => {
  if (createMode.value || !editMode.value) {
    router.back();
  } else {
    editMode.value = false;
  }
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const converToAnswerProps = (filters: IFilter[]) => {
  return filters.map((filter) => {
    const answerProp: IAnswerProp = {
      no: Number(filter.key),
      label: filter.label ?? "",
      type: filter.fieldType ?? "",
    };

    const selectedOption = Array.isArray(filter.selectedOptions)
      ? filter.selectedOptions?.[0]
      : filter.selectedOptions;

    if (answerProp.type === "option") {
      answerProp.selected = selectedOption?.key ?? "";
      answerProp.selectValue = selectedOption?.value ?? "";
    } else {
      answerProp.inputValue = selectedOption?.value ?? "";
    }

    return answerProp;
  });
};

/** 노선 수요조사 응답 등록/수정 */
const updateLineDemandSurveyAnswer = async () => {
  if (!lineDemandSurvey.value) return;

  const answerProps = converToAnswerProps(filters.value);

  showLoadingImage();
  const updateResult = await surveyStore.updateLineDemandSurveyAnswer(
    lineDemandSurvey.value.surveyUid,
    answerProps
  );

  if (!updateResult.isSuccess) {
    removeLoadingImage();
    handleError("노선 수요조사 신청 오류", updateResult);
    return;
  }

  await getLineDemandSurveyAnswer();
  removeLoadingImage();

  showToastMessage("노선 수요조사 신청이 완료되었습니다.");
  toggleCreateMode(false);
  toggleEditMode(false);
};

// 노선 수요조사 신청 취소 확인 모달
const shouldShowDeleteConfirmModal = ref(false);
const toggleShowDeleteConfirmModal = (show: boolean) => {
  shouldShowDeleteConfirmModal.value = show;
};

/** 노선 수요조사 응답 삭제 */
const deleteLineDemandSurveyAnswer = async () => {
  if (!lineDemandSurvey.value) return;

  showLoadingImage();
  const deleteResult = await surveyStore.deleteLineDemandSurveyAnswer(
    lineDemandSurvey.value.surveyUid
  );

  if (!deleteResult.isSuccess) {
    removeLoadingImage();
    handleError("노선 수요조사 신청 취소 오류", deleteResult);
    return;
  }

  await getLineDemandSurveyAnswer();
  removeLoadingImage();

  showToastMessage("노선 수요조사 신청이 취소되었습니다.");
  toggleCreateMode(true);
  toggleEditMode(true);
};

const goToBack = () => {
  router.currentRoute.value.meta.willGoBack = true;
  router.back();
};

const lineDemandSurvey = ref<ILineDemandSurvey>();
const filters = ref<IFilter[]>([]);

const getLineDemandSurveyList = async () => {
  const surveysResult = await surveyStore.getLineDemandSurveyList();

  if (!surveysResult.data?.length) {
    lineDemandSurvey.value = undefined;
    filters.value = [];

    showErrorModal(
      "안내",
      "수요조사 기간이 아닙니다",
      undefined,
      undefined,
      goToBack
    );
    return;
  }

  lineDemandSurvey.value = surveysResult.data[0];
};

const lineDemandSurveyAnswer = ref<ILineDemandSurveyAnswer>();

const getLineDemandSurveyAnswer = async () => {
  if (!lineDemandSurvey.value) return;

  const answerResult = await surveyStore.getLineDemandSurveyAnswer(
    lineDemandSurvey.value.surveyUid
  );

  filters.value = convertLineDemandSurveyToFilters(
    lineDemandSurvey.value,
    answerResult.data,
    userInfo.value
  );

  if (!answerResult.data) {
    // 현재 수조조사 설문에 답변이 없으면, 수요조사 응답 화면 표시
    toggleCreateMode(true);
    toggleEditMode(true);
    return;
  }

  lineDemandSurveyAnswer.value = answerResult.data;
};

onMounted(async () => {
  await getLineDemandSurveyList();
  await getLineDemandSurveyAnswer();
});

const __returned__ = { showLoadingImage, removeLoadingImage, showErrorModal, showToastMessage, surveyStore, userStore, userInfo, createMode, editMode, toggleCreateMode, toggleEditMode, handleClickBack, handleError, converToAnswerProps, updateLineDemandSurveyAnswer, shouldShowDeleteConfirmModal, toggleShowDeleteConfirmModal, deleteLineDemandSurveyAnswer, goToBack, lineDemandSurvey, filters, getLineDemandSurveyList, lineDemandSurveyAnswer, getLineDemandSurveyAnswer, Page, NavigationBar, RouteDemandSurveyEdit, RouteDemandSurveyDetail, Modal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})